import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import StyledQuill from '../shared/StyledQuill';
import { uploadFileToFirebase } from '../../utils/firebaseUploadUtils';
import ChevronIcon from '../icons/ChevronIcon'; // Import ChevronIcon
import EditIcon from '../icons/EditIcon'; // Import EditIcon
import { ViewIcon } from '../icons/ViewIcon'; // Import ViewIcon as named import
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
import {
    TimelineSection,
    TimelineItem,
    ActivityIcon,
    ActivityContent,
    ActivityMeta,
    Button,
    UserInfo,
    SectionTitle,
    QuillEditorContainer,
    ActivityHeader,
    ToggleButton,
    UserLink, // Import UserLink
    EditCommentActions, // Styles for edit comment actions
    CommentTextContainer // Styles for comment text container
} from '../../styles/CardModal.styles';

const CardActivity = ({
    cardData,
    comment,
    setComment,
    handleAddComment,
    handleUpdateComment, // Add handleUpdateComment
    isSaving,
    onAddAttachment,
    projectMembers // Add projectMembers to props
}) => {
    const quillRef = useRef(null);
    const [filterType, setFilterType] = useState('all'); // 'all' or 'comments'
    const [isActivityVisible, setIsActivityVisible] = useState(true); // New state for visibility
    const [editingCommentId, setEditingCommentId] = useState(null); // Track which comment is being edited
    const [editedCommentText, setEditedCommentText] = useState(''); // Track the edited comment text
    const [viewingHistoryCommentId, setViewingHistoryCommentId] = useState(null); // Track which comment's history is being viewed
    const { user } = useAuth(); // Get current user

    const toggleActivityVisibility = () => {
        setIsActivityVisible(!isActivityVisible);
    };

    const filteredActivities = () => {
        const activities = [...(cardData.comments || []), ...(cardData.activity || [])];
        if (filterType === 'comments') {
            return activities.filter(item => !!item.text) // Comments have a 'text' property
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
        return activities.sort((a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt));
    };

    const sortedActivities = filteredActivities();

    const handleImageUpload = async (file) => {
        try {
            const attachment = await uploadFileToFirebase(file, 'editor-images');
            if (attachment) {
                const editor = quillRef.current.getEditor();
                const range = editor.getSelection();
                editor.insertEmbed(range.index, 'image', attachment.url);
                onAddAttachment(attachment); // Add to attachments array
            }
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    };

    const handlePaste = (e) => {
        const clipboard = e.clipboardData;
        if (clipboard.files.length > 0) {
            e.preventDefault();
            Array.from(clipboard.files).forEach(file => {
                if (file.type.startsWith('image/')) {
                    handleImageUpload(file);
                } else {
                    // Handle non-image files as links
                    uploadFileToFirebase(file, 'editor-files').then(attachment => {
                        if (attachment) {
                            const editor = quillRef.current.getEditor();
                            const range = editor.getSelection();
                            editor.insertText(range.index, attachment.name, { 'link': attachment.url });
                            onAddAttachment(attachment); // Add to attachments array
                        }
                    });
                }
            });
        }
    };

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image']
        ],
        clipboard: {
            matchVisual: false,
        },
    };

    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link', 'image'
    ];

    const startEditComment = (commentItem) => {
        setEditingCommentId(commentItem.id);
        setEditedCommentText(commentItem.text);
    };

    const cancelEditComment = () => {
        setEditingCommentId(null);
        setEditedCommentText('');
    };

    const saveEditedComment = async () => {
        if (!editedCommentText.trim() || isSaving) return;
        await handleUpdateComment(editingCommentId, editedCommentText);
        setEditingCommentId(null);
        setEditedCommentText('');
    };

    const toggleHistoryView = (commentId) => {
        if (viewingHistoryCommentId === commentId) {
            setViewingHistoryCommentId(null); // Hide if currently showing
        } else {
            setViewingHistoryCommentId(commentId); // Show for this comment
        }
    };


    return (
        <TimelineSection>
            <ActivityHeader>
                <SectionTitle>Activity</SectionTitle>
                <ToggleButton onClick={toggleActivityVisibility} isExpanded={isActivityVisible}>
                    <ChevronIcon direction={isActivityVisible ? 'up' : 'down'} />
                    {isActivityVisible ? 'Hide' : 'Show'}
                </ToggleButton>
            </ActivityHeader>

            {isActivityVisible && (
                <>
                    <Button onClick={() => setFilterType(filterType === 'all' ? 'comments' : 'all')}>
                        {filterType === 'all' ? 'Show Comments Only' : 'Show All Activity'}
                    </Button>

                    <QuillEditorContainer>
                        <StyledQuill
                            ref={quillRef}
                            theme="snow"
                            value={comment}
                            onChange={setComment}
                            onPaste={handlePaste}
                            modules={modules}
                            formats={formats}
                            placeholder="Write a comment..."
                        />
                    </QuillEditorContainer>

                    <Button
                        onClick={handleAddComment}
                        disabled={!comment.trim() || isSaving}
                    >
                        Add Comment
                    </Button>

                    {sortedActivities.map(item => (
                        <TimelineItem key={item.id} data-testid={`comment-${item.id}`}>
                            <UserLink to={`/profile/${item.userId}`}>
                                <ActivityIcon>
                                    {projectMembers && projectMembers[item.userId] && projectMembers[item.userId].photoURL ? (
                                        <img
                                            src={projectMembers[item.userId].photoURL}
                                            alt=""
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '50%'
                                            }}
                                        />
                                    ) : (
                                        <span>{item.userName?.[0] || '•'}</span>
                                    )}
                                </ActivityIcon>
                            </UserLink>
                            <ActivityContent>
                                <UserInfo>
                                    {/* Render rich text content if available, otherwise plain text */}
                                    {editingCommentId === item.id ? (
                                        // This is the editing mode for comments, should remain as is.
                                        <QuillEditorContainer>
                                            <StyledQuill
                                                theme="snow"
                                                value={editedCommentText}
                                                onChange={setEditedCommentText}
                                                modules={modules}
                                                formats={formats}
                                            />
                                            <EditCommentActions>
                                                <Button onClick={saveEditedComment} disabled={isSaving}>Save</Button>
                                                <Button onClick={cancelEditComment} disabled={isSaving}>Cancel</Button>
                                            </EditCommentActions>
                                        </QuillEditorContainer>
                                    ) : (
                                        // Display mode for comments and actions
                                        <CommentTextContainer>
                                            {item.text ? (
                                                // This is a comment
                                                <>
                                                    <UserLink to={`/profile/${item.userId}`}>
                                                        {item.userName}
                                                    </UserLink>
                                                    <div dangerouslySetInnerHTML={{ __html: item.text }} />
                                                </>
                                            ) : (
                                                // This is an action log
                                                <div dangerouslySetInnerHTML={{ __html: `<a href="/profile/${item.userId}">${item.userName}</a> ${item.action}` }} />
                                            )}
                                            {item.text && item.userId === user?.uid && ( // Show edit icon only for user's own comments
                                                <EditIcon
                                                    onClick={() => startEditComment(item)}
                                                    style={{ cursor: 'pointer', marginLeft: '8px' }}
                                                    data-testid={`edit-icon-${item.id}`}
                                                    title="Edit comment"
                                                />
                                            )}
                                        </CommentTextContainer>
                                    )}
                                </UserInfo>
                                <ActivityMeta>
                                    {new Date(item.timestamp || item.createdAt).toLocaleString()}
                                    {item.history && item.history.length > 0 && <span> (edited)</span>}
                                    {item.history && item.history.length > 0 && (
                                        <ViewIcon
                                            onClick={() => toggleHistoryView(item.id)}
                                            style={{ cursor: 'pointer', marginLeft: '8px' }}
                                            data-testid={`view-history-icon-${item.id}`}
                                            title={viewingHistoryCommentId === item.id ? 'Hide edit history' : 'View edit history'} // Updated tooltip text
                                        />
                                    )}
                                </ActivityMeta>
                                {viewingHistoryCommentId === item.id && item.history && (
                                    <div>
                                        <strong>Version History:</strong>
                                        {item.history.slice().reverse().map((entry, index) => ( // Show newest first
                                            <TimelineItem key={`${item.id}-history-${index}`} style={{ marginLeft: '20px', fontSize: '0.9em' }}>
                                                <ActivityContent>
                                                    <div dangerouslySetInnerHTML={{ __html: entry.text }} />
                                                    <ActivityMeta>
                                                        Edited at: {new Date(entry.editedAt).toLocaleString()}
                                                    </ActivityMeta>
                                                </ActivityContent>
                                            </TimelineItem>
                                        ))}
                                    </div>
                                )}
                            </ActivityContent>
                        </TimelineItem>
                    ))}
                </>
            )}
        </TimelineSection>
    );
};

export default CardActivity;